<script setup lang="ts">
import IconBarcode from '~icons/mdi/barcode-scanner'
import IconMagnify from '~icons/mdi/magnify'
import type { SearchSuggestion, SuggestionType } from '~/types'

const searchStore = useSearchStore()
const route = useRoute()
const localePath = useLocalePath()
const { t } = useI18n()
const { searchRouteName } = useSearchRoute()

const search = ref('')
const {
  suggestionItems,
  resolveMatchedSupplierSuggestion,
  resetSearchSuggestions,
} = useSearchSuggestions(search)
const visibleSuggestionItems = computed(() => suggestionItems.value.slice(0, 10))

const searchRef = ref()
const isFocused = ref(false)
const isMenuOpen = ref(false)
const isEngaged = ref(false)

const selectedBySearch = ref<SearchSuggestion | null>(null)
const isNavigatingFromSearch = ref(false)
const isCleared = ref(false)

const draftQuery = ref('')
watch(search, (value) => {
  if (value) draftQuery.value = value
})

const routeQuery = computed(() => {
  const path = route.path
  if (path === localePath('/search') || path === localePath('/hybrid-search')) {
    return (route.query.q as string | undefined) ?? ''
  }
  return ''
})

const activeQuery = computed(() => draftQuery.value || routeQuery.value)

const selected = computed<SearchSuggestion | null>(() => {
  if (isCleared.value) return null
  if (activeQuery.value) {
    return { label: activeQuery.value, value: activeQuery.value, type: 'product' as SuggestionType }
  }
  return selectedBySearch.value
})

watch(() => route.fullPath, () => {
  isCleared.value = false
  if (!isNavigatingFromSearch.value) {
    selectedBySearch.value = null
  }
  isNavigatingFromSearch.value = false
  search.value = ''
  draftQuery.value = ''
  endSearchSession()
  resetSearchSuggestions()
})

const userStore = useUserStore()
const isHybridSearch = useIsHybridSearch()
const isDimActive = computed(() => isEngaged.value)

function beginSearchSession() {
  isEngaged.value = true
}

function endSearchSession() {
  isEngaged.value = false
  isMenuOpen.value = false
  searchRef.value?.blur()
}

function onDimUpdate(active: boolean) {
  if (active) return
  endSearchSession()
}

function onSearchKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    endSearchSession()
    return
  }
  beginSearchSession()
  if (event.key === 'Enter') onEnter()
}

function onSearchFocusOut(event: FocusEvent) {
  if (isMenuOpen.value) return
  const root = (searchRef.value as { $el?: HTMLElement } | undefined)?.$el
  const nextTarget = event.relatedTarget as Node | null
  if (root && nextTarget && root.contains(nextTarget)) return
  endSearchSession()
}

onMounted(() => {
  watch(
    () => [userStore.isAuthenticated, userStore.isUserDataPending, userStore.customerHandle] as const,
    ([authenticated, userDataPending, customerHandle]) => {
      if (!authenticated || userDataPending) return
      if (isHybridSearch.value && !customerHandle) return
      void searchStore.getSearchHistory()
    },
    { immediate: true },
  )
})

const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

function highlightMatch(text: string, query: string): string {
  if (!query || !text) return text
  const regex = new RegExp(`(${escapeRegex(query)})`, 'gi')
  return text.replace(regex, '<strong>$1</strong>')
}

function getTypeLabel(type: SuggestionType): string {
  if (type === 'supplier') return t('layout.search.suggestion.supplier')
  if (type === 'category') return t('layout.search.suggestion.category')
  return ''
}

async function onSelect(item: SearchSuggestion | string) {
  if (!item) return

  draftQuery.value = ''
  endSearchSession()

  if (typeof item === 'string') {
    searchStore.setSearchMeta(item, 'typed')
    await navigateTo({ path: localePath(searchRouteName.value), query: { q: item } })
    return
  }

  if ((item.type === 'supplier' || item.type === 'category') && item.url) {
    searchStore.setSearchMeta(item.label, `suggestion-${item.type}`)
    selectedBySearch.value = item
    isNavigatingFromSearch.value = true
    await navigateTo(localePath(item.url))
    return
  }

  searchStore.setSearchMeta(item.value, `suggestion-${item.type}`)
  await navigateTo({ path: localePath(searchRouteName.value), query: { q: item.value } })
}

function onEnter() {
  const query = search.value || draftQuery.value
  const supplierSuggestion = resolveMatchedSupplierSuggestion(query)
  if (supplierSuggestion) {
    onSelect(supplierSuggestion)
    return
  }
  if (query) onSelect(query)
}

function onClear() {
  search.value = ''
  draftQuery.value = ''
  selectedBySearch.value = null
  isCleared.value = true
  resetSearchSuggestions()
}

function onFocused(focused: boolean) {
  isFocused.value = focused

  if (focused) {
    if (!activeQuery.value) searchStore.getSearchHistory()
    return
  }

  nextTick(() => {
    if (!search.value && draftQuery.value) search.value = draftQuery.value
  })
}
</script>

<template>
  <Teleport to="body">
    <v-fade-transition>
      <div
        v-if="isDimActive"
        class="header-search-dim-page"
        @click="onDimUpdate(false)"
      />
    </v-fade-transition>
  </Teleport>

  <v-fade-transition>
    <div
      v-if="isDimActive"
      class="header-search-dim-bar"
      data-test-id="search-dim"
      @click="onDimUpdate(false)"
    />
  </v-fade-transition>

  <div class="d-flex justify-center w-100">
    <div class="d-flex align-center header-search__cluster mx-0 mx-md-2">
      <v-autocomplete
        id="search-input"
        ref="searchRef"
        v-model:search="search"
        v-model:menu="isMenuOpen"
        :model-value="selected"
        :items="visibleSuggestionItems"
        :placeholder="$t('layout.search.placeholder.full')"
        :class="[
          'header-search flex-grow-1',
          { 'header-search--elevated': isDimActive },
        ]"
        item-title="label"
        item-value="value"
        :filter-keys="['label', 'value']"
        :no-filter="true"
        clearable
        hide-details
        hide-no-data
        hide-selected
        return-object
        rounded="lg"
        variant="solo"
        flat
        density="compact"
        bg-color="white"
        color="black"
        transition="slide-y-transition"
        max-width="800"
        menu-icon=""
        :menu-props="{ maxWidth: '100%', maxHeight: 560, contentClass: 'header-search__menu' }"
        data-test-id="search-bar-input"
        @update:model-value="onSelect"
        @update:focused="onFocused"
        @click:clear="onClear"
        @keydown="onSearchKeydown"
        @mousedown="beginSearchSession"
        @focusout="onSearchFocusOut"
      >
        <!-- desktop search icon -->
        <template #prepend-inner>
          <v-icon
            :icon="IconMagnify"
            size="20"
            class="text-grey-8"
          />
        </template>

        <template #item="{ item, props }">
          <v-list-item
            v-bind="props"
            :title="undefined"
            @click="onSelect(item.raw)"
          >
            <template
              v-if="item.raw.type === 'supplier' && item.raw.imageUrl"
              #prepend
            >
              <NuxtImg
                width="32"
                height="32"
                class="me-2"
                :src="item.raw.imageUrl"
                :alt="item.raw.label"
              />
            </template>

            <v-list-item-title>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span v-html="highlightMatch(item.raw.label, search)" />
            </v-list-item-title>

            <v-list-item-subtitle
              v-if="item.raw.subtitle"
              class="text-caption"
            >
              {{ item.raw.subtitle }}
            </v-list-item-subtitle>

            <template #append>
              <span
                v-if="['supplier', 'category'].includes(item.raw.type)"
                class="text-caption text-grey ms-2"
              >
                {{ getTypeLabel(item.raw.type) }}
              </span>
            </template>
          </v-list-item>
        </template>

        <!-- mobile barcode button -->
        <template #append-inner>
          <v-btn
            :to="localePath('barcode')"
            variant="plain"
            rounded="lg"
            height="32"
            min-width="30"
            class="d-md-none text-none px-1 me-3 flex-shrink-0"
            data-nav-id="search_barcode"
            :aria-label="$t('layout.search.barcode')"
            @mousedown.stop
            @click.stop
          >
            <v-icon
              :icon="IconBarcode"
              size="30"
            />
          </v-btn>

          <!-- desktop search button -->
          <v-btn
            id="search-button"
            data-test-id="search-button"
            variant="flat"
            rounded="lg"
            color="red-600"
            height="32"
            min-width="32"
            class="text-none px-3 header-search__submit"
            data-track-id="search_submit"
            @mousedown.prevent
            @click="onEnter"
          >
            <v-icon
              :icon="IconMagnify"
              size="19"
              class="me-md-1"
            />
            <span class="d-none d-md-inline font-weight-bold">
              {{ $t('layout.search.placeholder.short') }}
            </span>
          </v-btn>
        </template>
      </v-autocomplete>

      <!-- desktop barcode button -->
      <v-btn
        id="barcode-button"
        :to="localePath('barcode')"
        color="red-600"
        rounded="lg"
        height="36"
        min-width="32"
        class="d-none d-md-inline-flex text-none bg-red-50 ms-6 px-3 flex-shrink-0 header-search__scan"
        data-nav-id="search_barcode"
        :aria-label="$t('layout.search.barcode')"
        @mousedown.stop
        @click.stop
      >
        <v-icon
          :icon="IconBarcode"
          size="19"
          class="me-xl-2"
        />
        <span class="d-none d-xl-inline font-weight-bold">
          {{ $t('layout.search.barcode') }}
        </span>
      </v-btn>
    </div>
  </div>
</template>

<style lang="scss">
.header-search-dim-page,
.header-search-dim-bar {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
}

.header-search-dim-page {
  z-index: 1001;
}

.header-search-dim-bar {
  z-index: 1;
}

.header-search--elevated {
  position: relative;
  z-index: 2;
}

.header-search__cluster {
  width: 100%;
  max-width: 800px;
}

.header-search__menu {
  overflow: hidden !important;
}

.header-search {
  .v-field {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .v-field--appended {
    padding-inline-end: 4px;
  }

  .v-field__prepend-inner {
    padding-inline-end: 0;
  }

  .v-autocomplete__menu-icon {
    display: none !important;
  }

  .v-field__input input {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &::placeholder {
      color: rgb(var(--v-theme-vm-grey-8));
      opacity: 1;
      text-overflow: ellipsis;
    }
  }

  .header-search__submit {
    background-color: rgb(var(--v-theme-red-600)) !important;
    color: #fff !important;

    .v-icon {
      color: #fff !important;
    }
  }
}

.header-search__scan {
  color: rgb(var(--v-theme-red-600)) !important;
  border-color: rgb(var(--v-theme-red-600)) !important;
  border-width: 1.5px !important;

  .v-icon {
    color: rgb(var(--v-theme-red-600)) !important;
  }
}
</style>
